// Crossy — Straße für Straße nach vorne. Endlos generierte Reihen aus Gras
// (mit Bäumen) und Straßen (mit Autos). Blocky-Look, alles prozedural.
import { useRef, useEffect } from "react";
import { drawSprite, drawBlockText, drawCoin, setupCanvas } from "./pixelArt";

const CELL = 40;
const MIN_COLS = 11;
const H = 640;
const VISIBLE_ROWS = Math.ceil(H / CELL) + 2;
const HOP_MS = 90;
const SCROLL_SPEED = 0.35; // Reihen pro Sekunde (Auto-Scroll)

const CAR_COLORS = [
  { body: "#d93a2b", dark: "#a82417" },
  { body: "#57b9e8", dark: "#3d97c4" },
  { body: "#e8c34e", dark: "#c4a136" },
  { body: "#9b6ee8", dark: "#7a4ec4" },
];

const PLAYER_PALETTE = {
  s: "#e8b98a", // Haut
  h: "#5a3d1e", // Haare
  t: "#3aa6a0", // Shirt
  d: "#2b7a76",
  p: "#4a4a6a", // Hose
  b: "#1f2410",
};
// Eigenkreation: kleine Blocky-Figur (von oben/vorne)
const PLAYER_SPRITE = [
  "..hhhh..",
  ".hssssh.",
  ".sbssbs.",
  ".ssssss.",
  ".tttttt.",
  "ttddddtt",
  ".pppppp.",
  ".pp..pp.",
];

export default function CrossyGame({ onGameOver, onStart, autoStart = false }) {
  const canvasRef = useRef(null);
  const onGameOverRef = useRef(onGameOver);
  onGameOverRef.current = onGameOver;
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;

  useEffect(() => {
    const canvas = canvasRef.current;
    // Spielfeldbreite ans Seitenverhältnis des Containers anpassen: auf dem
    // Desktop gibt es mehr Spalten links/rechts, nichts wird verzerrt.
    const availW = canvas.parentElement?.clientWidth || MIN_COLS * CELL;
    const availH = canvas.parentElement?.clientHeight || H;
    const aspect = Math.min(
      Math.max(availW / availH, (MIN_COLS * CELL) / H),
      3.2
    );
    const COLS = Math.max(MIN_COLS, Math.round((H * aspect) / CELL));
    const W = COLS * CELL;
    const CENTER_COL = Math.floor(COLS / 2);
    const ctx = setupCanvas(canvas, W, H);

    const state = {
      phase: "ready",
      rows: [], // index = Reihen-Nummer
      player: { col: CENTER_COL, row: 1, hopFrom: null, hopStart: 0 },
      cameraRow: 0, // unterste sichtbare Reihe (float)
      maxRow: 1,
      coins: 0,
      started: false,
      lastTime: 0,
    };

    const makeRow = (index) => {
      // Startbereich: sicheres Gras
      if (index < 4) return { type: "grass", trees: new Set() };

      const prev = state.rows[index - 1];
      const roadChance = prev && prev.type === "road" ? 0.45 : 0.55;
      if (Math.random() < roadChance) {
        const dir = Math.random() < 0.5 ? 1 : -1;
        const speed = 70 + Math.random() * 90;
        const cars = [];
        // Autos mit zufälligen, aber fairen Abständen vorbesetzen
        let x = Math.random() * 200;
        while (x < W + 200) {
          cars.push(x);
          x += 170 + Math.random() * 220;
        }
        return {
          type: "road",
          dir,
          speed,
          cars,
          color: CAR_COLORS[index % CAR_COLORS.length],
        };
      }
      const trees = new Set();
      // Baumdichte skaliert mit der Feldbreite (Basis: bis zu 3 auf 11 Spalten)
      const count = Math.floor(Math.random() * (4 * (COLS / MIN_COLS)));
      for (let i = 0; i < count; i++) {
        const c = Math.floor(Math.random() * COLS);
        if (c !== CENTER_COL || index > 6) trees.add(c);
      }
      // Selten liegt eine Münze auf dem Gras (10 Münzen = 1 gratis Download)
      let coin = null;
      if (index > 4 && Math.random() < 0.03) {
        const c = Math.floor(Math.random() * COLS);
        if (!trees.has(c)) coin = c;
      }
      return { type: "grass", trees, coin };
    };

    const ensureRows = (upTo) => {
      while (state.rows.length <= upTo) {
        state.rows.push(makeRow(state.rows.length));
      }
    };

    const reset = () => {
      state.rows = [];
      ensureRows(VISIBLE_ROWS + 4);
      state.player = { col: CENTER_COL, row: 1, hopFrom: null, hopStart: 0 };
      state.cameraRow = 0;
      state.maxRow = 1;
      state.coins = 0;
      state.started = false;
    };

    const die = () => {
      state.phase = "over";
      if (onGameOverRef.current)
        onGameOverRef.current(state.maxRow - 1, state.coins);
    };

    const tryMove = (dc, dr) => {
      const p = state.player;
      const nc = p.col + dc;
      const nr = p.row + dr;
      if (nc < 0 || nc >= COLS || nr < 0) return;
      if (nr < Math.floor(state.cameraRow)) return; // nicht hinter die Kamera
      ensureRows(nr + VISIBLE_ROWS);
      const row = state.rows[nr];
      if (row.type === "grass" && row.trees.has(nc)) return; // Baum blockiert
      p.hopFrom = { col: p.col, row: p.row };
      p.hopStart = performance.now();
      p.col = nc;
      p.row = nr;
      state.started = true;
      if (nr > state.maxRow) state.maxRow = nr;
      // Münze auf dem Zielfeld einsammeln
      if (row.type === "grass" && row.coin === nc) {
        row.coin = null;
        state.coins++;
      }
    };

    const update = (dt, now) => {
      ensureRows(Math.ceil(state.cameraRow) + VISIBLE_ROWS + 4);

      // Autos bewegen
      const lo = Math.floor(state.cameraRow) - 1;
      const hi = lo + VISIBLE_ROWS + 2;
      for (let r = Math.max(0, lo); r <= hi && r < state.rows.length; r++) {
        const row = state.rows[r];
        if (row.type !== "road") continue;
        const carLen = CELL * 1.8;
        for (let i = 0; i < row.cars.length; i++) {
          row.cars[i] += row.dir * row.speed * dt;
          // Wrap-around mit zufälligem Extra-Abstand
          if (row.dir > 0 && row.cars[i] > W + 100) {
            row.cars[i] = -carLen - 100 - Math.random() * 150;
          } else if (row.dir < 0 && row.cars[i] < -carLen - 100) {
            row.cars[i] = W + 100 + Math.random() * 150;
          }
        }
      }

      // Auto-Scroll sobald der Spieler losgelaufen ist
      if (state.started) {
        const targetCam = Math.max(state.cameraRow, state.player.row - 6);
        state.cameraRow += SCROLL_SPEED * dt;
        if (targetCam > state.cameraRow) {
          state.cameraRow += (targetCam - state.cameraRow) * Math.min(1, dt * 5);
        }
        // Hinter der Kamera rausgefallen?
        if (state.player.row < state.cameraRow - 0.2) {
          die();
          return;
        }
      }

      // Kollision mit Autos
      const p = state.player;
      const row = state.rows[p.row];
      if (row && row.type === "road") {
        const px = p.col * CELL + 6;
        const pw = CELL - 12;
        const carLen = CELL * 1.8;
        for (const carX of row.cars) {
          if (px + pw > carX + 6 && px < carX + carLen - 6) {
            die();
            return;
          }
        }
      }
    };

    const rowScreenY = (r) => H - (r + 1 - state.cameraRow) * CELL;

    const drawTree = (x, y) => {
      ctx.fillStyle = "#5a3d1e";
      ctx.fillRect(x + 14, y + 20, 12, 16);
      ctx.fillStyle = "#3a7018";
      ctx.fillRect(x + 4, y + 2, 32, 22);
      ctx.fillStyle = "#4e9420";
      ctx.fillRect(x + 8, y + 5, 14, 10);
    };

    const drawCar = (x, y, dir, color) => {
      const len = CELL * 1.8;
      ctx.fillStyle = color.dark;
      ctx.fillRect(x, y + 8, len, CELL - 14);
      ctx.fillStyle = color.body;
      ctx.fillRect(x + 3, y + 10, len - 6, CELL - 20);
      // Fenster vorne (je nach Richtung)
      ctx.fillStyle = "#bfe6f5";
      const winX = dir > 0 ? x + len - 26 : x + 8;
      ctx.fillRect(winX, y + 12, 18, 10);
      // Räder
      ctx.fillStyle = "#1f2410";
      ctx.fillRect(x + 6, y + CELL - 10, 12, 6);
      ctx.fillRect(x + len - 18, y + CELL - 10, 12, 6);
    };

    const draw = (now) => {
      ctx.fillStyle = "#0b0c0f";
      ctx.fillRect(0, 0, W, H);

      const lo = Math.max(0, Math.floor(state.cameraRow) - 1);
      const hi = lo + VISIBLE_ROWS + 1;

      for (let r = lo; r <= hi && r < state.rows.length; r++) {
        const row = state.rows[r];
        const y = rowScreenY(r);
        if (row.type === "grass") {
          for (let c = 0; c < COLS; c++) {
            ctx.fillStyle = (r + c) % 2 === 0 ? "#5d9e3f" : "#569539";
            ctx.fillRect(c * CELL, y, CELL, CELL);
          }
          for (const c of row.trees) drawTree(c * CELL, y);
          if (row.coin !== null && row.coin !== undefined) {
            drawCoin(ctx, row.coin * CELL + 6, y + 6, CELL - 12);
          }
        } else {
          ctx.fillStyle = "#4a4a4a";
          ctx.fillRect(0, y, W, CELL);
          // Fahrbahnmarkierung
          ctx.fillStyle = "#e8e8e0";
          for (let x = 8; x < W; x += 56) ctx.fillRect(x, y + CELL - 3, 24, 3);
          for (const carX of row.cars) drawCar(carX, y, row.dir, row.color);
        }
      }

      // Spieler (mit Hop-Animation)
      const p = state.player;
      let px = p.col * CELL;
      let py = rowScreenY(p.row);
      let lift = 0;
      if (p.hopFrom) {
        const t = Math.min(1, (now - p.hopStart) / HOP_MS);
        const fx = p.hopFrom.col * CELL;
        const fy = rowScreenY(p.hopFrom.row);
        px = fx + (px - fx) * t;
        py = fy + (py - fy) * t;
        lift = Math.sin(t * Math.PI) * 10;
        if (t >= 1) p.hopFrom = null;
      }
      // Schatten
      ctx.fillStyle = "rgba(0,0,0,0.25)";
      ctx.fillRect(px + 8, py + CELL - 8, CELL - 16, 6);
      drawSprite(ctx, PLAYER_SPRITE, PLAYER_PALETTE, px + 4, py - lift, 4);

      drawBlockText(ctx, `${state.maxRow - 1}`, W / 2, 40, 34);
      drawCoin(ctx, 12, 16, 20);
      drawBlockText(ctx, `${state.coins}`, 48, 27, 20);

      if (state.phase === "ready") {
        ctx.fillStyle = "rgba(10, 15, 10, 0.55)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "CROSSY", W / 2, H / 2 - 40, 46);
        drawBlockText(ctx, "Hüpf so weit du kommst!", W / 2, H / 2 + 5, 18);
        drawBlockText(ctx, "Taste / Tippen zum Start", W / 2, H / 2 + 40, 15);
      } else if (state.phase === "over") {
        ctx.fillStyle = "rgba(10, 15, 10, 0.55)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "GAME OVER", W / 2, H / 2 - 40, 40);
        drawBlockText(ctx, `${state.maxRow - 1} Reihen`, W / 2, H / 2 + 5, 24);
        drawBlockText(ctx, "Tippen für Neustart", W / 2, H / 2 + 45, 15);
      }
    };

    let raf;
    const loop = (now) => {
      const dt = Math.min(0.04, (now - state.lastTime) / 1000);
      state.lastTime = now;
      if (state.phase === "running") update(dt, now);
      draw(now);
      raf = requestAnimationFrame(loop);
    };

    const start = () => {
      reset();
      state.phase = "running";
      if (onStartRef.current) onStartRef.current();
    };

    const onKeyDown = (e) => {
      const dirKeys = {
        ArrowUp: [0, 1], w: [0, 1], W: [0, 1],
        ArrowDown: [0, -1], s: [0, -1], S: [0, -1],
        ArrowLeft: [-1, 0], a: [-1, 0], A: [-1, 0],
        ArrowRight: [1, 0], d: [1, 0], D: [1, 0],
      };
      if (dirKeys[e.key]) {
        e.preventDefault();
        if (state.phase === "running") tryMove(...dirKeys[e.key]);
        else start();
        return;
      }
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        if (state.phase === "running") tryMove(0, 1);
        else start();
      }
    };

    // Touch: Tippen = vorwärts, Wischen = Richtung
    let touchStart = null;
    let lastTouchTime = 0;
    const onTouchStart = (e) => {
      touchStart = e.touches[0];
    };
    const onTouchEnd = (e) => {
      lastTouchTime = performance.now();
      if (!touchStart) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStart.clientX;
      const dy = t.clientY - touchStart.clientY;
      touchStart = null;
      if (state.phase !== "running") {
        start();
        return;
      }
      if (Math.abs(dx) < 24 && Math.abs(dy) < 24) {
        tryMove(0, 1);
      } else if (Math.abs(dx) > Math.abs(dy)) {
        tryMove(dx > 0 ? 1 : -1, 0);
      } else {
        tryMove(0, dy < 0 ? 1 : -1);
      }
    };
    const onClick = () => {
      // Nach einem Touch feuert der Browser zusätzlich "click" — ignorieren
      if (performance.now() - lastTouchTime < 500) return;
      if (state.phase !== "running") start();
      else tryMove(0, 1);
    };

    window.addEventListener("keydown", onKeyDown);
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas.addEventListener("touchend", onTouchEnd, { passive: true });
    canvas.addEventListener("click", onClick);

    reset();
    state.lastTime = performance.now();
    if (autoStart) start(); // Start-Button auf der Seite: direkt losspielen
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKeyDown);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchend", onTouchEnd);
      canvas.removeEventListener("click", onClick);
    };
  }, [autoStart]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full touch-none select-none"
      style={{ imageRendering: "pixelated", objectFit: "contain" }}
    />
  );
}
